/**
 * AuthPolicySection — Vague 113 Wave 1l
 *
 * Admin-only panel under Paramètres → "Méthodes de connexion" to configure
 * which login methods are allowed for THIS organisation's users.
 *
 * Backend endpoints:
 *   - GET  /api/auth-policy        → current org policy
 *   - PUT  /api/auth-policy        → update toggles + domain rules
 *
 * Features:
 *   - 3 toggles : mot de passe / Google SSO / Microsoft SSO
 *   - Per-domain rules ("@cabinet-rh.fr" → Microsoft only)
 *   - Lockout safety : at least one method must remain enabled (backend
 *     refuses with 400 otherwise — we surface that error inline)
 *   - Superadmin override : `force_policy=true` locks the panel for OF admins
 */
import React, { useEffect, useState } from 'react';
import {
  Loader2, ShieldCheck, KeyRound, AlertTriangle, Plus, Trash2,
  Save, Lock, Unlock,
} from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';

const METHOD_LABELS = {
  password: 'Mot de passe',
  google: 'Google',
  microsoft: 'Microsoft',
};

export default function AuthPolicySection({ currentUser }) {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = ['super_admin', 'superadmin'].includes(
    (currentUser?.role || '').toLowerCase(),
  );

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/auth-policy');
      setPolicy(data);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Chargement impossible' });
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const updateField = (k, v) => setPolicy((p) => ({ ...(p || {}), [k]: v }));

  const addRule = () => setPolicy((p) => ({
    ...p,
    domain_rules: [...(p?.domain_rules || []), { pattern: '', required_method: 'microsoft' }],
  }));

  const removeRule = (idx) => setPolicy((p) => ({
    ...p,
    domain_rules: (p?.domain_rules || []).filter((_, i) => i !== idx),
  }));

  const updateRule = (idx, k, v) => setPolicy((p) => ({
    ...p,
    domain_rules: (p?.domain_rules || []).map((r, i) => (i === idx ? { ...r, [k]: v } : r)),
  }));

  const save = async () => {
    setSaving(true);
    try {
      // Filter out empty patterns before sending
      const payload = {
        allow_password: !!policy.allow_password,
        allow_google: !!policy.allow_google,
        allow_microsoft: !!policy.allow_microsoft,
        domain_rules: (policy.domain_rules || []).filter((r) => r.pattern && r.pattern.trim()),
      };
      if (isSuperAdmin) payload.force_policy = !!policy.force_policy;
      const { data } = await api.put('/auth-policy', payload);
      setPolicy(data);
      toast({ title: 'Politique enregistrée' });
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Sauvegarde impossible',
      });
    } finally { setSaving(false); }
  };

  if (loading || !policy) {
    return (
      <div className="soft-card p-6 flex items-center gap-2 text-[#6B6C85]" data-testid="auth-policy-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  const locked = policy.force_policy && !isSuperAdmin;
  const noneAllowed = !policy.allow_password && !policy.allow_google && !policy.allow_microsoft;

  return (
    <div className="space-y-4" data-testid="auth-policy-section">
      {/* Header card */}
      <div className="soft-card p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-[#4F46E5]" />
          </div>
          <div className="flex-1">
            <h2 className="text-[16px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Méthodes de connexion
            </h2>
            <p className="text-[13px] text-[#6B6C85] mt-1">
              Choisissez les méthodes que les membres de votre organisation peuvent utiliser pour se connecter à OFManager.
              Vous pouvez aussi imposer une méthode SSO pour certains domaines email (par ex. <code>@cabinet-rh-pro.fr</code> → Microsoft).
            </p>
            {locked && (
              <div className="mt-3 p-2.5 rounded-lg bg-[#FEF3C7] text-[#92400E] text-[12px] flex items-start gap-1.5" data-testid="auth-policy-locked-notice">
                <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                Cette politique a été verrouillée par OFManager. Contactez le support pour la modifier.
              </div>
            )}
            {isSuperAdmin && (
              <div className="mt-3 p-2.5 rounded-lg bg-[#EDE9FE] text-[#5B21B6] text-[12px] flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span><strong>Mode Superadmin</strong> : vous pouvez verrouiller cette politique pour cette org.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Method toggles */}
      <div className="soft-card p-5 space-y-3">
        <h3 className="text-[14px] font-bold text-[#1A1B2E] mb-2">Méthodes autorisées</h3>
        {noneAllowed && (
          <div className="p-2.5 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-[12px] flex items-start gap-1.5" data-testid="auth-policy-lockout-warning">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            Aucune méthode n'est autorisée — au moins une doit rester active sinon plus aucun membre ne pourra se connecter.
          </div>
        )}
        <MethodToggle
          testid="toggle-password"
          icon={KeyRound}
          label="Mot de passe (email + mot de passe + magic link)"
          checked={policy.allow_password}
          disabled={locked}
          onChange={(v) => updateField('allow_password', v)}
          description="Connexion classique par identifiant et mot de passe. Inclut aussi les liens magiques envoyés par email."
        />
        <MethodToggle
          testid="toggle-google"
          icon={GoogleIcon}
          label="Google SSO"
          checked={policy.allow_google}
          disabled={locked}
          onChange={(v) => updateField('allow_google', v)}
          description="« Continuer avec Google ». Nécessite que GOOGLE_CLIENT_ID soit configuré côté serveur."
        />
        <MethodToggle
          testid="toggle-microsoft"
          icon={MicrosoftIcon}
          label="Microsoft Entra ID (Azure AD) SSO"
          checked={policy.allow_microsoft}
          disabled={locked}
          onChange={(v) => updateField('allow_microsoft', v)}
          description="« Continuer avec Microsoft ». Comptes personnels Outlook.com et comptes 365 d'organisation."
        />
      </div>

      {/* Domain rules */}
      <div className="soft-card p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-[14px] font-bold text-[#1A1B2E]">Règles par domaine email</h3>
            <p className="text-[12px] text-[#6B6C85] mt-0.5">
              Forcez l'utilisation d'une méthode pour certains domaines (ex&nbsp;: tous les <code>@cabinet-rh.fr</code> doivent passer par Microsoft).
              La première règle qui matche s'applique. Utilisez <code>*</code> pour « tous les autres ».
            </p>
          </div>
          <button
            type="button"
            onClick={addRule}
            disabled={locked}
            data-testid="auth-policy-add-rule"
            className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 flex-shrink-0"
          >
            <Plus className="w-3.5 h-3.5" /> Ajouter une règle
          </button>
        </div>

        {(policy.domain_rules || []).length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#EFF0F6] bg-[#FAFAFE] p-4 text-center text-[12.5px] text-[#6B6C85]" data-testid="auth-policy-no-rules">
            Aucune règle. Tous les domaines peuvent choisir librement parmi les méthodes autorisées ci-dessus.
          </div>
        ) : (
          <ul className="space-y-2">
            {(policy.domain_rules || []).map((rule, idx) => (
              <li
                key={idx}
                data-testid={`auth-policy-rule-${idx}`}
                className="flex items-center gap-2 rounded-lg border border-[#EFF0F6] bg-white p-2.5"
              >
                <div className="text-[12px] font-semibold text-[#6B6C85] flex-shrink-0">Si l'email correspond à</div>
                <input
                  value={rule.pattern || ''}
                  onChange={(e) => updateRule(idx, 'pattern', e.target.value)}
                  disabled={locked}
                  placeholder="ex: cabinet-rh-pro.fr ou *"
                  data-testid={`auth-policy-rule-pattern-${idx}`}
                  className="flex-1 h-9 px-3 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] disabled:opacity-50"
                />
                <div className="text-[12px] font-semibold text-[#6B6C85] flex-shrink-0">→ obligatoirement</div>
                <select
                  value={rule.required_method || 'microsoft'}
                  onChange={(e) => updateRule(idx, 'required_method', e.target.value)}
                  disabled={locked}
                  data-testid={`auth-policy-rule-method-${idx}`}
                  className="h-9 px-2 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] disabled:opacity-50"
                >
                  <option value="microsoft">Microsoft</option>
                  <option value="google">Google</option>
                  <option value="password">Mot de passe</option>
                </select>
                <button
                  type="button"
                  onClick={() => removeRule(idx)}
                  disabled={locked}
                  data-testid={`auth-policy-rule-delete-${idx}`}
                  className="h-9 w-9 rounded-lg text-[#B91C1C] hover:bg-[#FEF2F2] inline-flex items-center justify-center disabled:opacity-50 flex-shrink-0"
                  title="Supprimer la règle"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Superadmin force_policy toggle */}
      {isSuperAdmin && (
        <div className="soft-card p-5">
          <h3 className="text-[14px] font-bold text-[#1A1B2E] mb-2">Verrouillage Superadmin</h3>
          <MethodToggle
            testid="toggle-force-policy"
            icon={Lock}
            label="Verrouiller cette politique pour l'admin de l'organisation"
            checked={policy.force_policy}
            disabled={false}
            onChange={(v) => updateField('force_policy', v)}
            description="Quand verrouillé, l'admin de l'organisation ne peut plus modifier ces réglages. Seul Superadmin peut déverrouiller."
          />
        </div>
      )}

      {/* Save bar */}
      <div className="sticky bottom-0 flex items-center justify-end gap-2 bg-white/95 backdrop-blur border-t border-[#EFF0F6] -mx-5 px-5 py-3">
        <button
          type="button"
          onClick={reload}
          disabled={saving}
          data-testid="auth-policy-reload"
          className="h-9 px-3 rounded-lg text-[12.5px] font-semibold text-[#6B6C85] hover:bg-[#F6F7FB]"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || locked}
          data-testid="auth-policy-save"
          className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
function MethodToggle({ icon: Icon, label, description, checked, disabled, onChange, testid }) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-xl border ${checked ? 'border-[#C7D2FE] bg-[#FAFAFE]' : 'border-[#EFF0F6] bg-white'} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-[#FAFAFE]'}`}
    >
      <div className="w-9 h-9 rounded-lg bg-white border border-[#EFF0F6] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#4F46E5]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-[#1A1B2E]">{label}</div>
        <div className="text-[11.5px] text-[#6B6C85] mt-0.5">{description}</div>
      </div>
      <div className="flex-shrink-0 mt-0.5">
        <input
          type="checkbox"
          data-testid={testid}
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 accent-[#4F46E5]"
        />
      </div>
    </label>
  );
}

// Tiny inline logos for the toggles (no extra imports)
function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.4 2.2 30 0 24 0 14.6 0 6.5 5.4 2.5 13.2l7.8 6.1C12.2 13.2 17.6 9.5 24 9.5z" />
      <path fill="#34A853" d="M46.1 24.6c0-1.6-.1-3.2-.4-4.6H24v9.1h12.4c-.6 2.9-2.2 5.3-4.7 6.9l7.3 5.7c4.3-4 6.8-9.8 6.8-17.1z" />
      <path fill="#FBBC05" d="M10.3 28.7c-.5-1.4-.8-2.9-.8-4.7s.3-3.3.8-4.7l-7.8-6.1C.9 16.4 0 20.1 0 24s.9 7.6 2.5 10.8l7.8-6.1z" />
      <path fill="#EA4335" d="M24 48c6 0 11.3-2 15.1-5.4l-7.3-5.7c-2.1 1.4-4.8 2.2-6.9 2.2-5.3 0-9.8-3.4-11.3-8.1l-6.5 5C9.5 39.5 16.2 44 24 44z" />
    </svg>
  );
}
function MicrosoftIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  );
}
